import { DocumentServicesObject } from '@wix/document-services-types';
import { PlatformApiMethods } from '@wix/editor-platform-host-integration-apis';
import {
  TPAHandler,
  TPAMessage,
  TPAMessageCallback,
} from '../../types/tpaMessage';

export interface EditorPublicDataHandlers {
  setValue: TPAHandler;
  getValue: TPAHandler;
  removeValue: TPAHandler;
}

export type PublicDataHandlerType = keyof EditorPublicDataHandlers;

export const createEditorPublicDataHandlers = (
  platformApiMethods: PlatformApiMethods,
  documentServicesAPI: DocumentServicesObject,
): EditorPublicDataHandlers => {
  const setValue = (msg: TPAMessage, callback: TPAMessageCallback) => {
    if (!msg.compId) {
      throw new Error(
        '"compId" should be present in the message for setValue handler',
      );
    }

    const compPointer = documentServicesAPI.components.get.byId(msg.compId);
    if (!compPointer) {
      throw new Error('Component pointer not found for setValue');
    }
    documentServicesAPI.tpa.data.set(
      compPointer,
      msg.data?.key,
      msg.data?.value,
      msg.data?.scope,
      callback,
    );
  };

  const getValue = async (msg: TPAMessage, callback: TPAMessageCallback) => {
    if (msg.data?.scope === documentServicesAPI.tpa.data.SCOPE.APP) {
      const [selectedComp] =
        await platformApiMethods.editor.selection.getSelectedComponents();

      if (!selectedComp) {
        throw new Error('Component should be selected for getValue handler');
      }

      const origCompData =
        documentServicesAPI.components.data.get(selectedComp);
      documentServicesAPI.tpaV2.data.app.get(
        origCompData.appDefId,
        msg.data?.key,
        callback,
      );
    } else {
      const compPointer = documentServicesAPI.components.get.byId(msg.compId);

      if (!compPointer) {
        throw new Error('Component pointer not found for getValue');
      }

      documentServicesAPI.tpa.data.comp.get(
        compPointer,
        msg.data?.key,
        callback,
      );
    }
  };

  const removeValue = (msg: TPAMessage, callback: TPAMessageCallback) => {
    const compPointer = documentServicesAPI.components.get.byId(msg.compId);

    if (!compPointer) {
      throw new Error('Component pointer not found for removeValue');
    }

    documentServicesAPI.tpa.data.remove(
      compPointer,
      msg.data?.key,
      msg.data?.scope,
      callback,
    );
  };

  return {
    setValue,
    getValue,
    removeValue,
  };
};
